import { useQuery } from '@tanstack/react-query';
import type { SubjectSlug, SchoolLevel } from '@jeraisonne/db';
import type { GraphConfig, ExerciseVisualBlock } from '@jeraisonne/ai';
import { supabase } from '@/lib/supabase';
import type { GenerateLog } from '@/lib/exercises/generate-client';

// ── Types ──────────────────────────────────────────────────────────────────────

export type ExerciseDetail = {
  id: string;
  chapter_id: string;
  title: string;
  statement_md: string;
  context_md: string | null;
  difficulty: 'easy' | 'medium' | 'hard';
  estimated_minutes: number | null;
  step_by_step_mode: boolean;
  requires_calculator: boolean;
  status: 'draft' | 'published' | 'archived' | 'awaiting_review';
  created_at: string;
  /** Auteur de l'exercice — gate l'édition (seul l'auteur ou un admin peut modifier). */
  created_by: string | null;
  ai_log: GenerateLog | null;
  graph_config: GraphConfig | null;
  visual_blocks: ExerciseVisualBlock[];
  illustration_url: string | null;
  illustration_prompt: string | null;
  /** true = exercice fondamental (banque de questions Warmup). */
  is_fundamental: boolean;
  fundamental_mode: string | null;
  /** Toujours v2 (exercise_blocks) depuis Zéro V1. */
  content_version: 'v2';
  /** Épreuve d'origine (extraction PDF) — pose le retour vers l'épreuve. */
  source_epreuve_id: string | null;
  /** true = exercice paramétrique (généré depuis un Blueprint, migration 0229). */
  is_parametric: boolean;
  /** Blueprint d'origine — pose le retour vers son générateur (édition de la spec). */
  generator_blueprint_id: string | null;
  chapter: { slug: string; title: string; subject: SubjectSlug; level: SchoolLevel } | null;
};

export type ChapterOption = {
  id: string;
  title: string;
  subject: SubjectSlug;
  level: SchoolLevel;
  label: string;
};

// ── Queries ───────────────────────────────────────────────────────────────────

export function useExerciseDetail(exerciseId: string) {
  return useQuery({
    queryKey: ['exercise-detail', exerciseId],
    enabled: !!exerciseId,
    queryFn: async (): Promise<ExerciseDetail | null> => {
      const { data, error } = await supabase
        .from('exercises')
        .select(
          `
          id, chapter_id, title, statement_md, context_md, difficulty,
          estimated_minutes, step_by_step_mode, requires_calculator, status, created_at, created_by, ai_log, graph_config, visual_blocks,
          illustration_url, illustration_prompt, is_fundamental, fundamental_mode, content_version,
          source_epreuve_id, is_parametric, generator_blueprint_id,
          chapters ( slug, title, subject, level )
        `,
        )
        .eq('id', exerciseId)
        .maybeSingle();

      if (error) throw new Error(error.message);
      if (!data) return null;

      const raw = data as any;
      return {
        ...raw,
        ai_log: (raw.ai_log as GenerateLog | null) ?? null,
        chapter: raw.chapters ?? null,
      };
    },
  });
}

export function usePublishedChapters() {
  return useQuery({
    queryKey: ['published-chapters'],
    queryFn: async (): Promise<ChapterOption[]> => {
      const { data, error } = await supabase
        .from('chapters')
        .select('id, title, subject, level')
        .eq('status', 'published')
        .order('level')
        .order('title');

      if (error) throw new Error(error.message);

      return (data ?? []).map((c: any) => ({
        id: c.id,
        title: c.title,
        subject: c.subject as SubjectSlug,
        level: c.level as SchoolLevel,
        label: `${c.subject} · ${c.level} — ${c.title}`,
      }));
    },
  });
}

export type LessonOption = {
  id: string;
  title: string;
  display_order: number;
};

export function useChapterLessons(chapterId: string) {
  return useQuery({
    queryKey: ['chapter-lessons', chapterId],
    enabled: !!chapterId,
    queryFn: async (): Promise<LessonOption[]> => {
      const { data, error } = await supabase
        .from('lessons')
        .select('id, title, display_order')
        .eq('chapter_id', chapterId)
        .eq('status', 'published')
        .order('display_order');

      if (error) throw new Error(error.message);

      return (data ?? []) as LessonOption[];
    },
  });
}

/** Compétences d'un chapitre — pour le sélecteur de l'éditeur fondamental. */
export type ChapterCompetence = { id: string; slug: string; title: string };

export function useChapterCompetences(chapterId: string) {
  return useQuery({
    queryKey: ['chapter-competences', chapterId],
    enabled: !!chapterId,
    queryFn: async (): Promise<ChapterCompetence[]> => {
      const { data, error } = await supabase
        .from('competences')
        .select('id, slug, title')
        .eq('chapter_id', chapterId)
        .order('display_order');

      if (error) throw new Error(error.message);
      return (data ?? []) as ChapterCompetence[];
    },
  });
}
